/**
 * Product Config tab — Liberty product assignments with effective date/time.
 * Angular-style: each row’s product / effective dates are read-only until its pencil
 * is clicked (then save again with the pencil). Delete is available whenever the
 * user may remove the row—no pencil step required. Header Save still flushes dirty
 * rows via PropertyEdit.
 */
import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react'
import { Calendar, Clock, Loader2, Package, Pencil, Plus, Trash2 } from 'lucide-react'
import { cn } from '@lib/utils/helpers'
import { useUserRole } from '@/hooks/useUserRole'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'
import { companyEditConfirmPresentation } from '@/features/company/constants'
import { SectionCard } from '@/components/ui/SectionCard'
import companyFormStyles from '@/features/company/styles/companyAdd.module.css'
import tabStyles from '@/features/company/styles/companyInfoTab.module.css'
import { DatePicker } from '@/components/ui/DatePicker'
import { showApiError, showError, showSuccess } from '@/lib/utils/notifications'
import {
  CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE,
  buildSelectedProductsJson,
  deletePropertyProductRow,
  extractPropertyProducts,
  extractProductCatalog,
  fetchCanEditProductMapping,
  fetchCanDeleteProductMapping,
} from '@/features/property/services/propertyProductService'
import type {
  PropertyProductConfigTabHandle,
  PropertyProductConfigTabProps,
} from '../types'

/* ────────────── helpers ────────────── */

function isBaseProductRow(r: Record<string, unknown>): boolean {
  if (Number(r.product_id) === 1) return true
  const isDefault = r.is_default === 1 || r.is_default === '1' || r.is_default === true
  if ((r.product_id == null || String(r.product_id).trim() === '') && isDefault) return true
  if (r.is_base === 1 || r.is_base === '1' || r.is_base === true) return true
  const code = String(r.product_code ?? '').toLowerCase()
  if (code === 'base') return true
  const name = String(r.product_name ?? r.name ?? '').toLowerCase().trim()
  return name === 'base'
}

function rowId(r: Record<string, unknown>): string | number | undefined {
  return (r.property_product_map_id ?? r.id ?? r.property_product_id ?? r.pp_id) as
    | string
    | number
    | undefined
}

/** Prefer server map id for DELETE; avoid accidental use of unrelated `id` shapes. */
function propertyProductMapIdForDelete(r: Record<string, unknown>): string | number | undefined {
  const m = r.property_product_map_id
  if (m != null && String(m).trim() !== '' && !String(m).startsWith('new-')) {
    return m as string | number
  }
  const id = r.id
  if (typeof id === 'number' && Number.isFinite(id)) return id
  if (typeof id === 'string' && /^\d+$/.test(id.trim())) return Number(id.trim())
  return undefined
}

/**
 * Angular parity (moment 'YYYY-MM-DD HH:mm:ss'): treat API naive datetimes as
 * browser-local wall clock for display/edit. ET is only used for "now"/case checks.
 */
function parseLocalDateTime(s: string | null | undefined): Date | null {
  if (!s || !String(s).trim()) return null
  const m = String(s)
    .trim()
    .match(/^(\d{4})-(\d{1,2})-(\d{1,2})(?:[ T](\d{1,2}):(\d{1,2})(?::(\d{1,2}))?)?/)
  if (!m) {
    const d = new Date(String(s))
    return Number.isNaN(d.getTime()) ? null : d
  }
  return new Date(
    Number(m[1]),
    Number(m[2]) - 1,
    Number(m[3]),
    Number(m[4] ?? 0),
    Number(m[5] ?? 0),
    Number(m[6] ?? 0),
    0
  )
}

function toInputDate(s: string | null | undefined): string {
  const d = parseLocalDateTime(s)
  if (!d) return ''
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

function toInputTime(s: string | null | undefined): string {
  const d = parseLocalDateTime(s)
  if (!d) return ''
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

/** Angular formatDateTime: local Date → naive 'YYYY-MM-DD HH:mm:ss' */
function combineDateTimeLocal(date: string, time: string): string | null {
  if (!date) return null
  const t = time && time.length >= 5 ? time : '00:00:00'
  const full = t.length === 5 ? `${t}:00` : t
  const [y, mo, dy] = date.split('-').map(Number)
  const [hh, mm, ss] = full.split(':').map((v) => parseInt(v, 10) || 0)
  if ([y, mo, dy].some((n) => Number.isNaN(n))) return null
  const d = new Date(y, mo - 1, dy, hh, mm, ss, 0)
  if (Number.isNaN(d.getTime())) return null
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')} ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}:${String(d.getSeconds()).padStart(2, '0')}`
}

function sortProductRows(rows: Record<string, unknown>[]): Record<string, unknown>[] {
  return [...rows.filter((r) => !isBaseProductRow(r)), ...rows.filter((r) => isBaseProductRow(r))]
}

/** Angular getCurrentEasternTime — only for Active/future/"tomorrow" rules */
function getCurrentEasternTime(): Date {
  return new Date(new Date().toLocaleString('en-US', { timeZone: 'America/New_York' }))
}

/** Angular convertToEasternTime */
function convertToEasternTime(date: Date): Date {
  return new Date(date.toLocaleString('en-US', { timeZone: 'America/New_York' }))
}

function getTomorrowDate(): Date {
  const d = getCurrentEasternTime()
  d.setDate(d.getDate() + 1)
  d.setHours(0, 0, 0, 0)
  return d
}

function parseDateStrToDate(s: string): Date | null {
  if (!s) return null
  const parts = s.split('-').map(Number)
  if (parts.length !== 3 || parts.some((p) => Number.isNaN(p))) return null
  return new Date(parts[0], parts[1] - 1, parts[2])
}

function getOtherProductDateRanges(
  rows: Record<string, unknown>[],
  excludeKey: string
): Array<{ start: Date; end: Date }> {
  const ranges: Array<{ start: Date; end: Date }> = []
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (rowKey(row, i) === excludeKey) continue
    if (isBaseProductRow(row)) continue

    const fromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
    const fromT = String(row._effective_from_time ?? toInputTime(row.effective_from as string))
    const toD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
    const toT = String(row._effective_to_time ?? toInputTime(row.effective_to as string))
    if (!fromD || !toD) continue
    const startText = combineDateTimeLocal(fromD, fromT)
    const endText = combineDateTimeLocal(toD, toT)
    if (!startText || !endText) continue
    const start = parseLocalDateTime(startText)
    const end = parseLocalDateTime(endText)
    if (!start || !end) continue
    ranges.push({ start, end })
  }
  return ranges
}

function isDateInBlockedRange(date: Date, ranges: Array<{ start: Date; end: Date }>): boolean {
  const dayStart = new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime()
  const dayEnd = new Date(date.getFullYear(), date.getMonth(), date.getDate(), 23, 59, 59, 999).getTime()
  for (const r of ranges) {
    if (r.start.getTime() <= dayStart && r.end.getTime() >= dayEnd) return true
  }
  return false
}

function checkDateTimeOverlap(
  rows: Record<string, unknown>[],
  currentKey: string,
  effectiveFrom: string,
  effectiveTo: string | null
): { overlap: boolean; productName?: string } {
  const fromA = parseLocalDateTime(effectiveFrom)?.getTime()
  if (fromA == null) return { overlap: false }
  const toA = effectiveTo ? (parseLocalDateTime(effectiveTo)?.getTime() ?? Infinity) : Infinity

  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    if (rowKey(row, i) === currentKey) continue
    if (isBaseProductRow(row)) continue

    const oFromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
    const oFromT = String(row._effective_from_time ?? toInputTime(row.effective_from as string))
    const oToD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
    const oToT = String(row._effective_to_time ?? toInputTime(row.effective_to as string))

    const oFromStr = combineDateTimeLocal(oFromD, oFromT)
    if (!oFromStr) continue
    const oToStr = oToD ? combineDateTimeLocal(oToD, oToT) : null

    const fromB = parseLocalDateTime(oFromStr)?.getTime()
    if (fromB == null) continue
    const toB = oToStr ? (parseLocalDateTime(oToStr)?.getTime() ?? Infinity) : Infinity

    if (fromA <= toB && fromB <= toA) {
      return { overlap: true, productName: displayProductName(row) }
    }
  }
  return { overlap: false }
}

function displayProductName(r: Record<string, unknown>): string {
  return String(r.product_name ?? r.name ?? r.title ?? r.product_code ?? '')
}

function catalogOptionLabel(r: Record<string, unknown>): string {
  return String(r.product_name ?? r.name ?? r.title ?? 'Product')
}

function catalogOptionValue(r: Record<string, unknown>): string {
  return String(r.product_id ?? r.id ?? '')
}

function rowKey(r: Record<string, unknown>, i: number): string {
  return rowId(r) != null ? String(rowId(r)) : `idx-${i}`
}

/** Server snapshot row that matches this UI row key (used when leaving a row without saving). */
function findServerRowForUiKey(
  key: string,
  property: Record<string, unknown> | null
): Record<string, unknown> | undefined {
  const list = sortProductRows(extractPropertyProducts(property))
  return list.find((row, i) => rowKey(row, i) === key)
}

function parseApiDateTime(s: unknown): number | null {
  if (s == null || s === '') return null
  const d = parseLocalDateTime(String(s))
  return d ? d.getTime() : null
}

function normalizeDateObj(date: Date | null, isEndOfDay = false): Date | null {
  if (!date) return null
  const d = new Date(date)
  if (isEndOfDay) d.setHours(23, 59, 59, 999)
  else d.setHours(0, 0, 0, 0)
  return d
}

function isDateInRangeObj(date: Date, fromDate: Date, toDate: Date): boolean {
  const d = normalizeDateObj(date)
  const f = normalizeDateObj(fromDate)
  const t = normalizeDateObj(toDate, true)
  if (!d || !f || !t) return false
  return d.getTime() >= f.getTime() && d.getTime() <= t.getTime()
}

function isDateRangeActiveObj(from: Date | null, to: Date | null, now: Date): boolean {
  if (!from) return false
  const fromDate = normalizeDateObj(from)
  if (!fromDate) return false
  const n = normalizeDateObj(now)
  if (!n) return false
  return to ? isDateInRangeObj(now, fromDate, to) : n.getTime() >= fromDate.getTime()
}

function getRowEffectiveDates(row: Record<string, unknown>): { from: Date | null; to: Date | null } {
  const fromD = String(row._effective_from_date ?? toInputDate(row.effective_from as string))
  const fromT = String(row._effective_from_time ?? toInputTime(row.effective_from as string))
  const toD = String(row._effective_to_date ?? toInputDate(row.effective_to as string))
  const toT = String(row._effective_to_time ?? toInputTime(row.effective_to as string))

  const fromStr = combineDateTimeLocal(fromD, fromT)
  const toStr = toD ? combineDateTimeLocal(toD, toT) : null

  return {
    from: fromStr ? parseLocalDateTime(fromStr) : null,
    to: toStr ? parseLocalDateTime(toStr) : null,
  }
}

function getMostRecentOpenProductRow(rows: Record<string, unknown>[], now: Date): { index: number; fromDate: Date } | null {
  let best: { index: number; fromDate: Date } | null = null
  rows.forEach((r, i) => {
    const { from, to } = getRowEffectiveDates(r)
    const n = normalizeDateObj(now)
    if (from && !to && n && n.getTime() >= normalizeDateObj(from)!.getTime()) {
      const fd = normalizeDateObj(from)!
      if (!best || fd.getTime() > best.fromDate.getTime()) {
        best = { index: i, fromDate: fd }
      }
    }
  })
  return best
}

function isOverriddenByOtherRow(idx: number, curFrom: Date | null, curTo: Date | null, rows: Record<string, unknown>[], now: Date): boolean {
  return rows.some((g, i) => {
    if (i === idx) return false
    const { from: oFrom, to: oTo } = getRowEffectiveDates(g)
    if (!oFrom || !oTo || !isDateInRangeObj(now, oFrom, oTo)) return false
    if (!curFrom || !curTo || !isDateInRangeObj(now, curFrom, curTo)) return true
    return normalizeDateObj(oFrom)!.getTime() > normalizeDateObj(curFrom)!.getTime()
  })
}

function isProductActive(row: Record<string, unknown>, index: number, rows: Record<string, unknown>[]): boolean {
  const pid = Number(row.product_id ?? 0)
  const isBase = isBaseProductRow(row) || pid === 1
  const now = getCurrentEasternTime()
  const { from, to } = getRowEffectiveDates(row)

  if (isBase) {
    return !rows.some((g) => {
      if (isBaseProductRow(g) || Number(g.product_id) === 1) return false
      const dates = getRowEffectiveDates(g)
      return isDateRangeActiveObj(dates.from, dates.to, now)
    })
  }

  if (rows.length === 1) return isDateRangeActiveObj(from, to, now)

  const openProduct = getMostRecentOpenProductRow(rows, now)
  if (openProduct && openProduct.index === index) return true

  return isDateRangeActiveObj(from, to, now) && !isOverriddenByOtherRow(index, from, to, rows, now)
}

function productDateCase(row: Record<string, unknown>): 'future' | 'active' | 'past' | 'unknown' {
  // Angular determineCaseType: compare convertToEasternTime(parsed local Date) vs getCurrentEasternTime()
  const now = getCurrentEasternTime().getTime()
  const fromParsed = row.effective_from ? parseLocalDateTime(String(row.effective_from)) : null
  const toParsed = row.effective_to ? parseLocalDateTime(String(row.effective_to)) : null
  const fromDate = fromParsed ? convertToEasternTime(fromParsed) : null
  const toDate = toParsed ? convertToEasternTime(toParsed) : null
  const from = fromDate && !Number.isNaN(fromDate.getTime()) ? fromDate.getTime() : null
  const to = toDate && !Number.isNaN(toDate.getTime()) ? toDate.getTime() : null
  if (from != null && from > now && (to == null || to > now)) return 'future'
  if (from != null && from <= now && (to == null || to > now)) return 'active'
  if (from != null && to != null && from < now && to < now) return 'past'
  return 'unknown'
}

/** True when non-base rows (including new drafts) differ from `property.current_products`. */
function rowsDifferFromPropertySnapshot(
  localRows: Record<string, unknown>[],
  property: Record<string, unknown> | null
): boolean {
  const serverRows = sortProductRows(extractPropertyProducts(property))
  const localNB = localRows.filter((r) => !isBaseProductRow(r))
  const serverNB = serverRows.filter((r) => !isBaseProductRow(r))
  const serverByMapId = new Map<string, Record<string, unknown>>()
  for (const s of serverNB) {
    const id = propertyProductMapIdForDelete(s) ?? rowId(s)
    if (id != null) serverByMapId.set(String(id), s)
  }

  for (const r of localNB) {
    const rid = rowId(r)
    if (rid != null && String(rid).startsWith('new-')) return true

    const mapId = propertyProductMapIdForDelete(r) ?? rid
    if (mapId == null) return true
    const s = serverByMapId.get(String(mapId))
    if (!s) return true

    if (String(r.product_id ?? '') !== String(s.product_id ?? '')) return true

    const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
    const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
    const localFromMs = parseApiDateTime(combineDateTimeLocal(fromD, fromT))
    const serverFromMs = parseApiDateTime(s.effective_from)
    if (localFromMs !== serverFromMs) return true

    const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
    const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
    const localTo = toD ? combineDateTimeLocal(toD, toT) : null
    const localToMs = localTo ? parseApiDateTime(localTo) : null
    const serverToMs = parseApiDateTime(s.effective_to)
    if (localToMs !== serverToMs) return true
  }

  return localNB.length !== serverNB.length
}

/** Same visual weight as DatePicker calendar icon (h-5). */
const DATE_FIELD_ICON = 'pointer-events-none absolute right-3 top-1/2 z-10 h-5 w-5 -translate-y-1/2 text-gray-400'

/** Matches native time input / browser clock scale (smaller than calendar). */
const TIME_FIELD_CLOCK_CLASS =
  'pointer-events-none absolute right-3 top-1/2 z-[1] h-4 w-4 -translate-y-1/2 text-gray-400'

const TIME_INPUT_CLASS =
  'box-border h-[42px] w-full min-w-[110px] rounded-md border border-slate-200 bg-white py-2.5 pl-4 pr-9 text-sm leading-5 focus:border-transparent focus:outline-none focus:ring-2 focus:ring-[rgb(21_151_255)] disabled:cursor-not-allowed disabled:bg-white disabled:text-slate-900 disabled:opacity-100 [&::-webkit-calendar-picker-indicator]:absolute [&::-webkit-calendar-picker-indicator]:right-0 [&::-webkit-calendar-picker-indicator]:top-0 [&::-webkit-calendar-picker-indicator]:h-full [&::-webkit-calendar-picker-indicator]:w-9 [&::-webkit-calendar-picker-indicator]:cursor-pointer [&::-webkit-calendar-picker-indicator]:opacity-0'

function TimeFieldInput({
  value,
  onChange,
  disabled,
  dataTestId,
}: {
  value: string
  onChange: (v: string) => void
  disabled?: boolean
  dataTestId?: string
}) {
  return (
    <div className="relative min-w-0">
      <input type="time" step={1} className={TIME_INPUT_CLASS} disabled={disabled} value={value} onChange={(e) => onChange(e.target.value)} data-testid={dataTestId} />
      <Clock strokeWidth={1.5} className={TIME_FIELD_CLOCK_CLASS} aria-hidden />
    </div>
  )
}

/** Matches DatePicker + time input: same widths, heights, and icon scale (calendar h-5, clock h-4). */
function ReadOnlyEffectiveFields({
  dateLabel,
  timeLabel,
  muted,
}: {
  dateLabel: string
  timeLabel: string
  muted?: boolean
}) {
  const textCls = muted ? 'text-slate-400' : 'text-slate-800'
  const dateBox =
    'box-border h-[42px] rounded-md border border-slate-200 bg-white py-2.5 pl-4 pr-10 text-sm leading-5 flex items-center'
  const timeBox =
    'box-border h-[42px] w-full whitespace-nowrap rounded-md border border-slate-200 bg-white py-2.5 pl-4 pr-9 text-sm leading-5 flex items-center'
  return (
    <div className="grid w-full min-w-0 grid-cols-[minmax(145px,3fr)_minmax(130px,2fr)] items-center gap-2">
      <div className="relative min-w-0">
        <div className={cn(dateBox, textCls)}>{dateLabel || '--/--/----'}</div>
        <Calendar className={DATE_FIELD_ICON} aria-hidden />
      </div>
      <div className="relative min-w-0">
        <div className={cn(timeBox, textCls)}>{timeLabel || '--:--:--'}</div>
        <Clock strokeWidth={1.5} className={TIME_FIELD_CLOCK_CLASS} aria-hidden />
      </div>
    </div>
  )
}

/* ────────────── component ────────────── */

export const PropertyProductConfigTab = forwardRef<PropertyProductConfigTabHandle, PropertyProductConfigTabProps>(
  function PropertyProductConfigTab({ property, propertyId: _propertyId, onRefresh, onRequestSave }, ref) {
    void _propertyId
    const { hasPolicy } = useUserRole()
    const canAdd = hasPolicy(['allow_product_config'])
    const canEdit = hasPolicy(['property_productconfig_edit'])
    const canDelete = hasPolicy(['property_productconfig_delete'])

    const [rows, setRows] = useState<Record<string, unknown>[]>([])
    const [catalog, setCatalog] = useState<Record<string, unknown>[]>([])
    const [loading, setLoading] = useState(true)
    const [savingId, setSavingId] = useState<string | null>(null)
    const [deleteTarget, setDeleteTarget] = useState<{
      id: string | number
      label: string
      message: string
    } | null>(null)
    /** Map id while GET can_delete_product_mapping runs (Angular pre-delete check). */
    const [deletePrecheckMapId, setDeletePrecheckMapId] = useState<string | number | null>(null)
    /** UI row key (`rowKey`) currently in inline-edit mode; only that row’s fields are enabled. */
    const [editingRowKey, setEditingRowKey] = useState<string | null>(null)
    const [effectiveToOnlyRowKey, setEffectiveToOnlyRowKey] = useState<string | null>(null)

    const rowsRef = useRef(rows)
    rowsRef.current = rows
    const propertyRef = useRef(property)
    propertyRef.current = property

    const loadProducts = useCallback(() => {
      setLoading(true)
      try {
        const list = extractPropertyProducts(property)
        setRows(sortProductRows(list))
        setEditingRowKey(null)
        setEffectiveToOnlyRowKey(null)
      } finally {
        setLoading(false)
      }
    }, [property])

    useEffect(() => {
      loadProducts()
    }, [loadProducts])

    useEffect(() => {
      const embedded = extractProductCatalog(property)
      setCatalog(embedded)
      if (extractPropertyProducts(property).length === 0) {
        const base = embedded.find((product) => Number(product.product_id ?? product.id) === 1)
        setRows([{ ...(base ?? { product_id: 1, product_name: 'Base' }), is_base: 1 }])
      }
    }, [property])

    const updateRow = (key: string, patch: Record<string, unknown>) => {
      if (editingRowKey !== key) return
      setRows((prev) =>
        prev.map((r, i) => {
          const k = rowKey(r, i)
          return k === key ? { ...r, ...patch } : r
        })
      )
    }

    const revertRowToServerSnapshot = useCallback((key: string) => {
      const serverRow = findServerRowForUiKey(key, propertyRef.current)
      setRows((prevRows) => {
        if (!serverRow) {
          return prevRows.filter((r, i) => rowKey(r, i) !== key)
        }
        return prevRows.map((r, i) => (rowKey(r, i) === key ? { ...serverRow } : r))
      })
    }, [])

    const handleAddRow = () => {
      if (!canAdd) return
      const currentDraft = rows.find((row) => String(rowId(row) ?? '').startsWith('new-'))
      if (currentDraft) {
        const productId = Number(currentDraft.product_id ?? 0)
        const fromDate = String(
          currentDraft._effective_from_date ??
            toInputDate(currentDraft.effective_from as string),
        )
        const fromTime = String(
          currentDraft._effective_from_time ??
            toInputTime(currentDraft.effective_from as string),
        )
        if (!productId) {
          showError('Product is required before adding another product.')
          return
        }
        if (!combineDateTimeLocal(fromDate, fromTime)) {
          showError('Effective From date and time are required before adding another product.')
          return
        }
      }
      const tempId = `new-${Date.now()}`
      const newKey = String(tempId)
      setRows((prev) =>
        sortProductRows([
          {
            id: tempId,
            product_id: '',
            product_name: '',
            effective_from: null,
            effective_to: null,
          },
          ...prev.filter((r) => !isBaseProductRow(r)),
          ...prev.filter((r) => isBaseProductRow(r)),
        ])
      )
      setEditingRowKey(newKey)
      setEffectiveToOnlyRowKey(null)
    }

    const upsertRowByKey = useCallback(
      async (key: string, rowSnapshot: Record<string, unknown>[]): Promise<boolean> => {
        const r = rowSnapshot.find((row, i) => rowKey(row, i) === key)
        if (!r || isBaseProductRow(r)) return true

        const pid = Number(r.product_id ?? 0)
        if (!pid) {
          showError('Product is required.')
          return false
        }

        const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
        const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
        const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
        const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
        const effective_from = combineDateTimeLocal(fromD, fromT)
        const effective_to = toD ? combineDateTimeLocal(toD, toT) : null
        if (!effective_from) {
          showError('Effective From date and time are required.')
          return false
        }

        const isNewDraft = String(rowId(r) ?? '').startsWith('new-')
        const nonBaseRows = rowSnapshot.filter((row) => !isBaseProductRow(row))
        const rowPosition = nonBaseRows.findIndex(
          (row) => rowKey(row, rowSnapshot.indexOf(row)) === key,
        )
        let validationRows = rowSnapshot
        if (isNewDraft) {
          const tomorrow = getTomorrowDate()
          const fromDateObj = parseLocalDateTime(effective_from)
          if (!fromDateObj || fromDateObj.getTime() < tomorrow.getTime()) {
            showError('Effective From date must be tomorrow or later for new products.')
            return false
          }
          const nextRow = rowPosition === 0 ? nonBaseRows[1] : undefined
          const nextToDate = nextRow
            ? String(
                nextRow._effective_to_date ??
                  toInputDate(nextRow.effective_to as string),
              )
            : ''
          if (nextRow && !nextToDate) {
            const closeDate = parseDateStrToDate(fromD)
            if (closeDate) {
              closeDate.setDate(closeDate.getDate() - 1)
              const closeDateText = `${closeDate.getFullYear()}-${String(closeDate.getMonth() + 1).padStart(2, '0')}-${String(closeDate.getDate()).padStart(2, '0')}`
              const nextKey = rowKey(nextRow, rowSnapshot.indexOf(nextRow))
              validationRows = rowSnapshot.map((row, index) =>
                rowKey(row, index) === nextKey
                  ? {
                      ...row,
                      _effective_to_date: closeDateText,
                      _effective_to_time: '23:59:59',
                    }
                  : row,
              )
              rowsRef.current = validationRows
              setRows(validationRows)
            }
          }
        }

        if (rowPosition > 0 && !effective_to) {
          showError('Effective To date and time are required for this product.')
          return false
        }

        if (effective_to) {
          const fromMs = parseLocalDateTime(effective_from)?.getTime()
          const toMs = parseLocalDateTime(effective_to)?.getTime()
          if (fromMs == null || toMs == null || toMs < fromMs) {
            showError('Effective To date/time must be on or after Effective From.')
            return false
          }
        }

        const overlapCheck = checkDateTimeOverlap(validationRows, key, effective_from, effective_to)
        if (overlapCheck.overlap) {
          showError(
            `Date range overlaps with "${overlapCheck.productName || 'another product'}". No two products can have overlapping effective dates.`
          )
          return false
        }

        // Persist via selected_products on main property POST (Angular) — no /api/property/product.
        return true
      },
      []
    )

    const buildSelectedProductsFromRows = useCallback((snapshot: Record<string, unknown>[]) => {
      const items = snapshot
        .filter((r) => !isBaseProductRow(r))
        .map((r) => {
          const fromD = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
          const fromT = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
          const toD = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
          const toT = String(r._effective_to_time ?? toInputTime(r.effective_to as string))
          const mapId = propertyProductMapIdForDelete(r)
          return {
            product_id: Number(r.product_id ?? 0),
            effective_from: combineDateTimeLocal(fromD, fromT),
            effective_to: toD ? combineDateTimeLocal(toD, toT) : null,
            property_product_map_id:
              mapId != null && !String(mapId).startsWith('new-') ? Number(mapId) : undefined,
            status: r.status,
          }
        })
      return buildSelectedProductsJson(items)
    }, [])

    const handleSaveRow = async (key: string) => {
      const r = rows.find((row, i) => rowKey(row, i) === key)
      if (!r || isBaseProductRow(r)) return

      setSavingId(key)
      let committed = false
      try {
        const ok = await upsertRowByKey(key, rowsRef.current)
        if (!ok) return
        setEditingRowKey(null)
        setEffectiveToOnlyRowKey(null)
        committed = true
        // Angular persists products only on main property Save (selected_products).
        onRequestSave?.()
      } catch (err) {
        showApiError(err)
      } finally {
        setSavingId(null)
        if (committed) setEditingRowKey(null)
      }
    }

    const handlePencilClick = async (key: string) => {
      const row = rowsRef.current.find((candidate, index) => rowKey(candidate, index) === key)
      const isDraft = Boolean(row && String(rowId(row) ?? '').startsWith('new-'))
      if ((!isDraft && !canEdit) || (isDraft && !canAdd) || savingId != null) return
      if (editingRowKey === key) {
        void handleSaveRow(key)
        return
      }
      const mapId = row ? propertyProductMapIdForDelete(row) : undefined
      const dateCase = row ? productDateCase(row) : 'unknown'
      if (mapId != null && dateCase !== 'future') {
        setSavingId(key)
        try {
          const result = await fetchCanEditProductMapping(mapId)
          if (!result.allowed) {
            if (dateCase === 'active') {
              setEffectiveToOnlyRowKey(key)
            } else {
              showError(result.message || 'This product mapping cannot be edited.')
              return
            }
          } else {
            setEffectiveToOnlyRowKey(null)
          }
        } catch (error) {
          showApiError(error)
          return
        } finally {
          setSavingId(null)
        }
      }
      if (dateCase === 'future') setEffectiveToOnlyRowKey(null)
      if (editingRowKey != null) {
        revertRowToServerSnapshot(editingRowKey)
      }
      setEditingRowKey(key)
    }

    const requestDeleteRow = async (r: Record<string, unknown>, label: string) => {
      const mapId = propertyProductMapIdForDelete(r)
      if (mapId == null) {
        showError('Cannot remove this row: missing product map id.')
        return
      }
      if (productDateCase(r) === 'future') {
        setDeleteTarget({
          id: mapId,
          label,
          message: 'Are you sure you want to delete this product configuration?',
        })
        return
      }
      setDeletePrecheckMapId(mapId)
      try {
        const { allowed, message } = await fetchCanDeleteProductMapping(mapId)
        if (!allowed) {
          showError(message ?? CANNOT_DELETE_PRODUCT_MAPPING_MESSAGE)
          return
        }
        setDeleteTarget({
          id: mapId,
          label,
          message: message?.trim() || 'Are you sure you want to delete this product configuration?',
        })
      } catch (err) {
        showApiError(err)
      } finally {
        setDeletePrecheckMapId(null)
      }
    }

    const handleDelete = async () => {
      if (!deleteTarget) return
      setSavingId(String(deleteTarget.id))
      try {
        await deletePropertyProductRow(deleteTarget.id)
        showSuccess('Product assignment removed.')
        await onRefresh()
      } catch (err) {
        showApiError(err)
      } finally {
        setSavingId(null)
        setDeleteTarget(null)
      }
    }

    const saveProductsIfDirtyFromParent = useCallback(
      async (_opts?: { silentSuccess?: boolean }): Promise<boolean> => {
        const prop = propertyRef.current
        let snapshot = rowsRef.current
        if (!rowsDifferFromPropertySnapshot(snapshot, prop)) return true
        const keys = snapshot.map((row, i) => rowKey(row, i))
        setSavingId('__bulk__')
        setEditingRowKey(null)
        try {
          for (const key of keys) {
            const row = snapshot.find((x, i) => rowKey(x, i) === key)
            if (!row || isBaseProductRow(row)) continue
            const ok = await upsertRowByKey(key, snapshot)
            if (!ok) return false
            snapshot = rowsRef.current
          }
          // Persistence is via selected_products on the parent property POST.
          return true
        } catch (err) {
          showApiError(err)
          return false
        } finally {
          setSavingId(null)
        }
      },
      [upsertRowByKey]
    )

    useImperativeHandle(
      ref,
      () => ({
        getProductConfigDirty: () =>
          rowsDifferFromPropertySnapshot(rowsRef.current, propertyRef.current) && (canAdd || canEdit),
        saveProductsIfDirtyFromParent,
        getSelectedProductsJson: () => buildSelectedProductsFromRows(rowsRef.current),
      }),
      [canAdd, canEdit, saveProductsIfDirtyFromParent, buildSelectedProductsFromRows]
    )

    return (
      <div
        className={`${tabStyles.tabContent} animate-in fade-in duration-300`}
        data-testid="property-product-config-tab"
      >
        <SectionCard
          icon={Package}
          title="Product Information"
          testId="property-product-config-section"
          titleTestId="property-product-config-section-title"
          infoContent={<span>All time in ET</span>}
          infoTestId="property-product-config-et-info"
          headerActions={
            canAdd ? (
              <button
                type="button"
                onClick={handleAddRow}
                disabled={loading || savingId != null}
                data-testid="property-product-config-add-button"
                className={cn(companyFormStyles.btnPrimary, 'inline-flex items-center gap-1.5')}
              >
                {loading ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden /> : <Plus className="h-4 w-4" aria-hidden />}
                Add New Product
              </button>
            ) : null
          }
        >
        <div className="w-full min-w-0 overflow-x-auto rounded-lg border border-slate-200 [&_input:disabled]:!bg-white [&_input:disabled]:!opacity-100 [&_select:disabled]:!bg-white [&_select:disabled]:!opacity-100">
          <table className="w-full min-w-[780px] table-fixed text-left text-xs sm:text-sm" data-testid="property-product-config-table">
            <thead className="border-b border-slate-200 bg-slate-50/80">
              <tr>
                <th className="w-[20%] whitespace-nowrap px-2 py-2.5 text-xs font-medium text-slate-500 sm:px-4 sm:py-3">Product <span className="font-semibold text-[#dc2626]">*</span></th>
                <th className="w-[36%] whitespace-nowrap px-2 py-2.5 text-xs font-medium text-slate-500 sm:px-4 sm:py-3">Effective From</th>
                <th className="w-[36%] whitespace-nowrap px-2 py-2.5 text-xs font-medium text-slate-500 sm:px-4 sm:py-3">Effective To</th>
                <th className="w-[8%] whitespace-nowrap px-2 py-2.5 text-xs font-medium text-slate-500 sm:px-4 sm:py-3">Action</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={4} className="px-4 py-12 text-center text-slate-500">
                  <Loader2 className="h-6 w-6 animate-spin inline mr-2 align-middle" />Loading…
                </td></tr>
              )}
              {!loading && rows.map((r, i) => {
                const key = rowKey(r, i)
                const base = isBaseProductRow(r)
                const isNewDraft = String(rowId(r) ?? '').startsWith('new-')
                const mapDelId = propertyProductMapIdForDelete(r)
                const deleteDisabledByPolicy = !isNewDraft && mapDelId == null
                const rowFieldsEnabled = (isNewDraft ? canAdd : canEdit) && editingRowKey === key
                const effectiveToOnly = effectiveToOnlyRowKey === key
                const rowActionBusy =
                  savingId === '__bulk__' ||
                  savingId === key ||
                  (mapDelId != null && savingId != null && String(savingId) === String(mapDelId))
                const deleteButtonDisabled =
                  (!isNewDraft && !canDelete) ||
                  rowActionBusy ||
                  deleteDisabledByPolicy ||
                  (deletePrecheckMapId != null && deletePrecheckMapId === mapDelId)
                const fromDate = String(r._effective_from_date ?? toInputDate(r.effective_from as string))
                const fromTime = String(r._effective_from_time ?? toInputTime(r.effective_from as string))
                const toDate = String(r._effective_to_date ?? toInputDate(r.effective_to as string))
                const toTime = String(r._effective_to_time ?? toInputTime(r.effective_to as string))

                const blockedRanges = getOtherProductDateRanges(rows, key)
                const tomorrowDateObj = getTomorrowDate()
                const filterDateFn = (date: Date) => !isDateInBlockedRange(date, blockedRanges)
                const effectiveToMinDate = fromDate ? parseDateStrToDate(fromDate) ?? tomorrowDateObj : tomorrowDateObj

                return (
                  <tr key={`${key}-${i}`} className="border-b border-slate-100">
                    {/* Product */}
                    <td className="px-2 sm:px-4 py-3 sm:py-4 align-top">
                      {base ? (
                        <span className="flex h-[42px] w-full items-center rounded-md border border-slate-200 bg-white px-3 text-sm leading-5 text-slate-900">
                          Base
                        </span>
                      ) : (
                        <select
                          className="box-border h-[42px] w-full rounded-md border border-slate-200 px-3 text-sm leading-5 focus:border-[rgb(21_151_255)] focus:outline-none bg-white appearance-none disabled:bg-white disabled:text-slate-900 disabled:opacity-100"
                          disabled={!rowFieldsEnabled || effectiveToOnly || savingId === key || savingId === '__bulk__'}
                          data-testid={`property-product-config-row-${key}-product-select`}
                          value={String(r.product_id ?? '')}
                          onChange={(e) => {
                            const v = e.target.value
                            const opt = catalog.find((c) => catalogOptionValue(c) === v)
                            updateRow(key, { product_id: v, product_name: opt ? catalogOptionLabel(opt) : '' })
                          }}
                        >
                          <option value="">Select Product</option>
                          {catalog
                            .filter((c) => Number(catalogOptionValue(c)) !== 1 && catalogOptionLabel(c).toLowerCase().trim() !== 'base')
                            .map((c, j) => (
                              <option key={j} value={catalogOptionValue(c)}>{catalogOptionLabel(c)}</option>
                            ))}
                          {(() => {
                            const v = String(r.product_id ?? '')
                            const lab = displayProductName(r)
                            if (!v || Number(v) === 1 || catalog.some((c) => catalogOptionValue(c) === v)) return null
                            return <option value={v}>{lab || `Product ${v}`}</option>
                          })()}
                        </select>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1.5">
                        {base && (
                          <span className="rounded bg-[rgb(21_151_255)] px-2 py-0.5 text-xs font-semibold text-white">
                            Default
                          </span>
                        )}
                        {isProductActive(r, i, rows) && (
                          <span className="rounded bg-emerald-500 px-2 py-0.5 text-xs font-semibold text-white">
                            Active
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Effective From */}
                    <td className="px-2 sm:px-4 py-3 sm:py-4 align-top">
                      <div className="grid w-full min-w-0 grid-cols-[minmax(145px,3fr)_minmax(130px,2fr)] items-center gap-2">
                        {/* Angular: Base uses same date+time controls (readonly), not a different display format */}
                        <div className="min-w-0">
                          <DatePicker
                            value={fromDate}
                            onChange={(v) => updateRow(key, { _effective_from_date: v })}
                            disabled={base || !rowFieldsEnabled || effectiveToOnly || savingId === key || savingId === '__bulk__'}
                            allowFutureDates={true}
                            minDate={base ? undefined : tomorrowDateObj}
                            filterDate={base ? undefined : filterDateFn}
                            showHelperText={false}
                            placeholder="MM/DD/YYYY"
                            className="text-sm !bg-white !text-slate-900 !opacity-100"
                            popperPlacement="top-start"
                            popperFixed
                            dataTestId={`property-product-config-row-${key}-effective-from-date`}
                          />
                        </div>
                        <TimeFieldInput
                          disabled={base || !rowFieldsEnabled || effectiveToOnly || savingId === key || savingId === '__bulk__'}
                          value={fromTime}
                          onChange={(v) => updateRow(key, { _effective_from_time: v })}
                          dataTestId={`property-product-config-row-${key}-effective-from-time`}
                        />
                      </div>
                    </td>

                    {/* Effective To */}
                    <td className="px-2 sm:px-4 py-3 sm:py-4 align-top">
                      <div className="grid w-full min-w-0 grid-cols-[minmax(145px,3fr)_minmax(130px,2fr)] items-center gap-2">
                        {base && !isNewDraft ? (
                          /* Angular Base Effective To: fixed dashes, not a real date/time */
                          <div className="col-span-2 min-w-0">
                            <ReadOnlyEffectiveFields
                              muted
                              dateLabel="--/--/----"
                              timeLabel="--:--:--"
                            />
                          </div>
                        ) : (
                          <>
                            <div className="min-w-0">
                              <DatePicker
                                value={toDate}
                                onChange={(v) => updateRow(key, { _effective_to_date: v })}
                                disabled={!rowFieldsEnabled || savingId === key || savingId === '__bulk__'}
                                allowFutureDates={true}
                                minDate={effectiveToMinDate}
                                maxDate={new Date(2099, 11, 31)}
                                filterDate={filterDateFn}
                                showHelperText={false}
                                placeholder="MM/DD/YYYY"
                                className="text-sm !bg-white !text-slate-900 !opacity-100"
                                popperPlacement="top-start"
                                popperFixed
                                dataTestId={`property-product-config-row-${key}-effective-to-date`}
                              />
                            </div>
                            <TimeFieldInput
                              disabled={!rowFieldsEnabled || savingId === key || savingId === '__bulk__'}
                              value={toTime}
                              onChange={(v) => updateRow(key, { _effective_to_time: v })}
                              dataTestId={`property-product-config-row-${key}-effective-to-time`}
                            />
                          </>
                        )}
                      </div>
                    </td>

                    {/* Action */}
                    <td className="px-2 sm:px-4 py-3 sm:py-4 align-top">
                      {!base && (isNewDraft || canEdit || canDelete) ? (
                        <div className="flex h-[42px] items-center gap-2">
                          <button
                            type="button"
                            title={editingRowKey === key ? 'Save' : 'Edit'}
                            aria-label={editingRowKey === key ? 'Save row' : 'Edit row'}
                            disabled={(!isNewDraft && !canEdit) || savingId != null}
                            data-testid={`property-product-config-row-${key}-save-button`}
                            className={cn(
                              'rounded border p-1.5 disabled:opacity-50',
                              editingRowKey === key
                                ? 'border-[rgb(21_151_255)]/40 bg-[rgb(21_151_255)]/10 text-[rgb(21_151_255)] hover:bg-[rgb(21_151_255)]/15'
                                : 'border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-[rgb(21_151_255)]',
                            )}
                            onClick={() => void handlePencilClick(key)}
                          >
                            {savingId === key ? <Loader2 className="h-4 w-4 animate-spin" /> : <Pencil className="h-4 w-4" />}
                          </button>
                          <button
                            type="button"
                            title={
                              isNewDraft
                                ? 'Remove draft row'
                                : 'Delete'
                            }
                            disabled={deleteButtonDisabled}
                            data-testid={`property-product-config-row-${key}-delete-button`}
                            className="rounded border border-slate-200 p-1.5 text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                            onClick={() => {
                              if (isNewDraft) {
                                setRows((prev) => prev.filter((x) => String(rowId(x) ?? '') !== String(rowId(r))))
                                if (editingRowKey === key) setEditingRowKey(null)
                              } else {
                                void requestDeleteRow(r, displayProductName(r) || 'this row')
                              }
                            }}
                          >
                            {deletePrecheckMapId != null && deletePrecheckMapId === mapDelId ? (
                              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                            ) : (
                              <Trash2 className="h-4 w-4" aria-hidden />
                            )}
                          </button>
                        </div>
                      ) : null}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
        </SectionCard>

        <ConfirmDialog
          isOpen={deleteTarget != null}
          title="Delete Product"
          message={
            deleteTarget?.message ||
            (deleteTarget ? `Remove "${deleteTarget.label}" from this property?` : '')
          }
          primaryLabel="Delete"
          onPrimary={() => void handleDelete()}
          secondaryLabel="Cancel"
          onSecondary={() => setDeleteTarget(null)}
          {...companyEditConfirmPresentation}
          overlayDataTestId="property-product-config-delete-confirm-overlay"
          primaryButtonDataTestId="property-product-config-delete-confirm-remove-button"
          secondaryButtonDataTestId="property-product-config-delete-confirm-cancel-button"
          closeButtonDataTestId="property-product-config-delete-confirm-close-button"
        />
      </div>
    )
  }
)

PropertyProductConfigTab.displayName = 'PropertyProductConfigTab'
